import { CommonModule, CurrencyPipe } from '@angular/common';
import {
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  ViewChild,
  computed,
  inject,
  signal
} from '@angular/core';
import { UwBadge } from '../../../shared/ui/badge/uw-badge';
import { UwDate, type UwDateValue } from '../../../shared/ui/date';
import { UwIcon } from '../../../shared/ui/icon/uw-icon';
import { UwTooltip } from '../../../shared/directives/tooltip.directive';
import {
  SUBMISSION_TABLE_LAB_APPLICATION_OPTIONS,
  SUBMISSION_TABLE_LAB_COLUMN_OPTIONS,
  SUBMISSION_TABLE_LAB_STATUS_OPTIONS,
  SubmissionTableLabColumnKey,
  SubmissionTableLabRow
} from './submission-table-lab.models';
import { submissionTableLabAppAdapterProvider } from './submission-table-lab-app.adapter';
import { SubmissionTableLabService } from './submission-table-lab.service';
import { uw } from './data';

type MenuPosition = {
  top: number;
  left: number;
  width: number;
};

type QueueMode = 'all' | 'PROCESSED' | 'PROCEED' | 'FLAGGED' | 'DECLINE';

@Component({
  selector: 'uw-submission-table-lab',
  standalone: true,
  providers: [SubmissionTableLabService, submissionTableLabAppAdapterProvider],
  imports: [
    CommonModule,
    CurrencyPipe,
    UwBadge,
    UwDate,
    UwIcon,
    UwTooltip
  ],
  templateUrl: './submission-table-lab.component.html',
  styles: [`
    :host {
      display: block;
    }

    .submission-lab {
      font-size: 11.5px;
      line-height: 1.35;
    }

    .submission-lab .submission-lab-toolbar :is(button, input, select, label),
    .submission-lab .submission-lab-table :is(button, input, select, label, td, th) {
      font-size: 11.5px;
      line-height: 1.3;
    }

    .submission-lab .submission-lab-table th,
    .submission-lab .submission-lab-table td {
      padding-top: 0.25rem;
      padding-bottom: 0.25rem;
    }

    .submission-lab .submission-lab-action {
      width: 1.625rem;
      height: 1.625rem;
    }
  `]
})
export class SubmissionTableLabComponent implements OnInit, OnDestroy {
  private readonly table = inject(SubmissionTableLabService);
  @ViewChild('submissionStatusTrigger') private submissionStatusTrigger?: ElementRef<HTMLButtonElement>;
  @ViewChild('applicationTypeTrigger') private applicationTypeTrigger?: ElementRef<HTMLButtonElement>;
  @ViewChild('underwriterTrigger') private underwriterTrigger?: ElementRef<HTMLButtonElement>;
  @ViewChild('queueModeTrigger') private queueModeTrigger?: ElementRef<HTMLButtonElement>;
  @ViewChild('submissionStatusMenu') private submissionStatusMenu?: ElementRef<HTMLElement>;
  @ViewChild('applicationTypeMenu') private applicationTypeMenu?: ElementRef<HTMLElement>;
  @ViewChild('underwriterMenu') private underwriterMenu?: ElementRef<HTMLElement>;
  @ViewChild('queueModeMenu') private queueModeMenu?: ElementRef<HTMLElement>;
  @ViewChild('tableColumnsTrigger') private tableColumnsTrigger?: ElementRef<HTMLButtonElement>;
  @ViewChild('tableColumnsPopover') private tableColumnsPopover?: ElementRef<HTMLElement>;



  readonly underwriterOptions = uw as Array<{ sr_no: number | string; name: string }>;
  readonly tableColumnOptions = SUBMISSION_TABLE_LAB_COLUMN_OPTIONS;
  readonly submissionStatusOptions = SUBMISSION_TABLE_LAB_STATUS_OPTIONS;
  readonly applicationTypeOptions = SUBMISSION_TABLE_LAB_APPLICATION_OPTIONS;
  readonly pageLimitOptions = signal<number[]>([...this.table.pageLimitOptions]);

  readonly rows = this.table.rows;
  readonly pageLimit = this.table.pageLimit;
  readonly totalCount = this.table.totalCount;
  readonly currentPage = this.table.currentPage;
  readonly columnFilters = this.table.filters;
  readonly tableColumnVisibility = this.table.tableColumnVisibility;
  readonly tableColumnOrder = this.table.tableColumnOrder;
  readonly visibleRows = this.table.visibleRows;
  readonly hasActiveFilters = this.table.hasActiveFilters;
  readonly activeFilterItems = this.table.activeFilterItems;
  readonly activeFilterValueCount = this.table.activeFilterValueCount;
  readonly totalPages = this.table.totalPages;
  readonly pagerItems = this.table.pagerItems;

  readonly tableStateMessage = this.table.tableStateMessage;
  readonly tableStateTone = this.table.tableStateTone;
  readonly canToggleUnderwriterScope = this.table.canToggleUnderwriterScope;
  readonly showAllSubmissions = this.table.showAllSubmissions;
  readonly actionFilter = this.table.actionFilter;
  readonly renderedColumnKeys = computed(() =>
    this.tableColumnOrder().filter((key) => this.tableColumnVisibility()[key] !== false)
  );
  readonly visibleColumnCount = computed(() => this.renderedColumnKeys().length);
  readonly trackByTableColumnKey = (_index: number, key: SubmissionTableLabColumnKey) => key;
  readonly submissionStatusMenuOpen = signal(false);
  readonly submissionStatusMenuPosition = signal<MenuPosition | null>(null);
  readonly applicationTypeMenuOpen = signal(false);
  readonly applicationTypeMenuPosition = signal<MenuPosition | null>(null);
  readonly underwriterMenuOpen = signal(false);
  readonly underwriterMenuPosition = signal<MenuPosition | null>(null);
  readonly queueModeMenuOpen = signal(false);
  readonly queueModeMenuPosition = signal<MenuPosition | null>(null);
  readonly tableColumnsPopoverOpen = signal(false);
  readonly underwriterSearchTerm = signal('');
  readonly underwriterScrollOptions = { suppressScrollX: true };
  readonly questionManagementOpen = signal(false);
  readonly transientSocketStatus = this.table.transientSocketStatus;
  readonly tableHeaderWidths = this.table.tableHeaderWidths;
  readonly submissionRowClass = (row: SubmissionTableLabRow) => ({
    'submission-row--pointer': false,
    'submission-row--default': true,
    'submission-row--processed-entry': this.table.showActionProcessed(row)
  });
  readonly appTypeLabels = [
    { key: '499', value: 'SSIC GL (CAS)' },
    { key: '492', value: 'SSIC Property' },
    { key: '485', value: 'SSIC - XSHS' },
    { key: '495', value: 'SSIC Excess' },
    { key: '483', value: 'Digital Rater (AL,PD,Cargo)' }

  ] as const;
  readonly queueModeOptions: Array<{ value: QueueMode; label: string; icon?: string }> = [
    { value: 'all', label: 'All', icon: 'sliders-horizontal' },
    { value: 'PROCESSED', label: 'PROCESSED SUBMISSIONS', icon: 'badge-check' },
    { value: 'PROCEED', label: 'EVALUATION PROCEED', icon: 'arrow-right' },
    { value: 'FLAGGED', label: 'EVALUATION FLAGGED', icon: 'alert-triangle' },
    { value: 'DECLINE', label: 'EVALUATION DECLINE', icon: 'x-circle' }
  ];
  readonly underwriterScopeOptions = [
    { value: 'mine', label: 'My submissions' },
    { value: 'all', label: 'All submissions' }
  ] as const;
  readonly submissionStatusSelectionLabel = computed(() => this.getSelectionLabel('submissionStatus'));
  readonly applicationTypeSelectionLabel = computed(() => this.getSelectionLabel('applicationType'));
  readonly underwriterSelectionLabel = computed(() => this.getSelectionLabel('underwriter', 'Search'));
  readonly queueModeSelectionLabel = computed(
    () => this.queueModeOptions.find((option) => option.value === this.actionFilter())?.label ?? 'All'
  );
  readonly filteredUnderwriterOptions = computed(() => {
    const query = this.normalizeUnderwriterSearchText(this.underwriterSearchTerm());

    if (!query) {
      return this.underwriterOptions;
    }

    return this.underwriterOptions.filter(
      (option) => this.matchesUnderwriterSearch(option.name, query) || String(option.sr_no).includes(query)
    );
  });

  private headerResizeState: { key: SubmissionTableLabColumnKey | 'action'; startX: number; startWidth: number } | null = null;
  private headerDragState: { key: SubmissionTableLabColumnKey; from: number } | null = null;

  ngOnInit(): void {
    this.table.initialize();
  }

  ngOnDestroy(): void {
    this.table.dispose();
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  }

  @HostListener('document:click', ['$event'])
  protected onDocumentClick(event: MouseEvent): void {
    const target = event.target as Node | null;

    if (!target) {
      return;
    }

    if (this.isClickInsideMenuOrTrigger(target)) {
      return;
    }

    this.closeAllMenus();
  }

  @HostListener('document:mousemove', ['$event'])
  protected onDocumentMouseMove(event: MouseEvent): void {
    if (!this.headerResizeState) {
      return;
    }

    event.preventDefault();

    const deltaX = event.clientX - this.headerResizeState.startX;
    const nextWidth = Math.max(90, this.headerResizeState.startWidth + deltaX);

    this.tableHeaderWidths.update((current) => ({
      ...current,
      [this.headerResizeState!.key]: nextWidth
    }));
  }

  @HostListener('document:mouseup')
  protected onDocumentMouseUp(): void {
    this.headerResizeState = null;
    this.headerDragState = null;
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  }

  @HostListener('document:dragend')
  protected onDocumentDragEnd(): void {
    this.headerDragState = null;
  }

  protected voiceId(prefix: string, ...parts: Array<string | number | null | undefined>): string {
    const safeParts = parts
      .filter((part) => part !== null && part !== undefined && String(part).trim() !== '')
      .map((part) =>
        String(part)
          .trim()
          .toLowerCase()
          .replace(/[^a-z0-9_-]+/g, '-')
      );

    return [prefix, ...safeParts].join('-');
  }

  protected stopPropagation(event: Event): void {
    event.stopPropagation();
  }

  protected suppressHeaderEvent(event: Event): void {
    event.stopPropagation();
    event.preventDefault();
  }

  protected getColumnFilterValue(key: SubmissionTableLabColumnKey): string {
    return this.columnFilters()[key] ?? '';
  }

  protected updateColumnFilter(key: SubmissionTableLabColumnKey, event: Event): void {
    const value = (event.target as HTMLInputElement | null)?.value ?? '';
    this.updateColumnFilterValue(key, value);
  }

  protected updateColumnFilterValue(key: SubmissionTableLabColumnKey, value: string): void {
    this.table.updateFilter(key, value);
  }

  protected handleDateFilterChange(key: 'submitDate' | 'effectiveDate', value: UwDateValue): void {
    const nextValue = typeof value === 'string'
      ? value
      : value?.start ?? value?.end ?? '';

    this.updateColumnFilterValue(key, nextValue);
  }

  protected clearColumnFilter(key: SubmissionTableLabColumnKey): void {
    if (key === 'submissionStatus') {
      this.closeSubmissionStatusMenu();
    }
    if (key === 'applicationType') {
      this.closeApplicationTypeMenu();
    }
    if (key === 'underwriter') {
      this.closeUnderwriterMenu();
    }

    this.table.clearFilter(key);
  }

  protected toggleTableColumnsPopover(event?: Event): void {
    if (event) {
      this.suppressHeaderEvent(event);
    }

    if (this.tableColumnsPopoverOpen()) {
      this.closeTableColumnsPopover();
      return;
    }

    this.closeAllMenus();
    this.tableColumnsPopoverOpen.set(true);
  }

  protected toggleQueueModeMenu(event?: Event): void {
    if (event) {
      this.suppressHeaderEvent(event);
    }

    if (this.queueModeMenuOpen()) {
      this.closeQueueModeMenu();
      return;
    }

    this.closeAllMenus();
    this.queueModeMenuPosition.set(this.buildPopoverPosition(this.queueModeTrigger?.nativeElement, 220, 320));
    this.queueModeMenuOpen.set(true);
  }

  protected toggleSubmissionStatusMenu(event?: Event): void {
    if (event) {
      this.suppressHeaderEvent(event);
    }

    if (this.submissionStatusMenuOpen()) {
      this.closeSubmissionStatusMenu();
      return;
    }

    this.closeAllMenus();
    this.submissionStatusMenuPosition.set(this.buildPopoverPosition(this.submissionStatusTrigger?.nativeElement, 220, 320));
    this.submissionStatusMenuOpen.set(true);
  }

  protected toggleApplicationTypeMenu(event?: Event): void {
    if (event) {
      this.suppressHeaderEvent(event);
    }

    if (this.applicationTypeMenuOpen()) {
      this.closeApplicationTypeMenu();
      return;
    }

    this.closeAllMenus();
    this.applicationTypeMenuPosition.set(this.buildPopoverPosition(this.applicationTypeTrigger?.nativeElement, 220, 320));
    this.applicationTypeMenuOpen.set(true);
  }

  protected toggleUnderwriterMenu(event?: Event): void {
    if (event) {
      this.suppressHeaderEvent(event);
    }

    if (this.underwriterMenuOpen()) {
      this.closeUnderwriterMenu();
      return;
    }

    this.closeAllMenus();
    this.underwriterSearchTerm.set(this.getColumnFilterValue('underwriter'));
    this.underwriterMenuPosition.set(this.buildPopoverPosition(this.underwriterTrigger?.nativeElement, 260, 360));
    this.underwriterMenuOpen.set(true);
  }

  protected closeTableColumnsPopover(): void {
    this.tableColumnsPopoverOpen.set(false);
  }

  protected closeQueueModeMenu(): void {
    this.queueModeMenuOpen.set(false);
    this.queueModeMenuPosition.set(null);
  }

  protected closeSubmissionStatusMenu(): void {
    this.submissionStatusMenuOpen.set(false);
    this.submissionStatusMenuPosition.set(null);
  }

  protected closeApplicationTypeMenu(): void {
    this.applicationTypeMenuOpen.set(false);
    this.applicationTypeMenuPosition.set(null);
  }

  protected closeUnderwriterMenu(): void {
    this.underwriterMenuOpen.set(false);
    this.underwriterMenuPosition.set(null);
    this.underwriterSearchTerm.set('');
  }

  protected openQuestionManagement(): void {
    this.questionManagementOpen.set(true);
  }

  protected closeQuestionManagement(): void {
    this.questionManagementOpen.set(false);
  }

  protected openDeclineReason(row: SubmissionTableLabRow): void {
    console.info('Lab decline reason action requested for', row.id);
  }

  protected openRiskAppetite(row: SubmissionTableLabRow): void {
    console.info('Lab risk appetite action requested for', row.id);
  }

  protected openSubmissionDetails(row: SubmissionTableLabRow): void {
    this.table.openSubmissionDetails(row);
  }

  protected handleTableActivate(_activateEvent?: { type?: string; row?: SubmissionTableLabRow; event?: Event }): void {
    // Intentionally left as a no-op in the lab.
  }

  protected handleLimitChange(event: Event): void {
    const value = Number((event.target as HTMLSelectElement).value);
    this.table.updatePageLimit(value);
  }

  protected goToPage(page: number): void {
    this.table.goToPage(page);
  }

  protected handleTableSort(event: any): void {
    // const nextSort = event.sorts?.[0];
    // const sortKey = nextSort?.prop as SubmissionTableLabColumnKey | undefined;

    // if (!sortKey || !nextSort?.dir) {
    //   this.table.sortState.set(null);
    //   return;
    // }

    // this.table.sortState.set({
    //   key: sortKey,
    //   direction: nextSort.dir
    // });
  }

  protected toggleSort(key: SubmissionTableLabColumnKey): void {
    this.table.setSort(key);
  }

  protected getSortIconName(key: SubmissionTableLabColumnKey): string {
    return this.table.getSortIconName(key);
  }

  protected isTableColumnVisible(key: SubmissionTableLabColumnKey): boolean {
    return this.table.hasColumnVisible(key);
  }

  protected updateTableColumnVisibility(key: SubmissionTableLabColumnKey, event: Event): void {
    const checked = (event.target as HTMLInputElement | null)?.checked === true;
    this.table.setColumnVisibility(key, checked);
  }

  protected resetTableColumns(): void {
    this.table.resetColumns();
  }

  protected dropTableHeader(targetKey: SubmissionTableLabColumnKey, event: DragEvent): void {
    event.preventDefault();

    const sourceKey = event.dataTransfer?.getData('text/plain') as SubmissionTableLabColumnKey | undefined;

    if (!sourceKey || sourceKey === targetKey) {
      return;
    }

    this.table.moveColumn(sourceKey, targetKey);
  }

  protected startTableHeaderDrag(key: SubmissionTableLabColumnKey, event: DragEvent): void {
    if (!event.dataTransfer) {
      return;
    }

    this.headerDragState = {
      key,
      from: this.tableColumnOrder().indexOf(key)
    };

    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', key);
  }

  protected allowTableHeaderDrop(event: DragEvent): void {
    event.preventDefault();
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'move';
    }
  }

  protected startTableHeaderResize(key: SubmissionTableLabColumnKey | 'action', event: MouseEvent): void {
    const headerElement = event.currentTarget as HTMLElement | null;

    if (!headerElement) {
      return;
    }

    const rect = headerElement.getBoundingClientRect();
    const isResizeZone = rect.right - event.clientX <= 12;

    if (!isResizeZone) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';

    this.headerResizeState = {
      key,
      startX: event.clientX,
      startWidth: this.getTableHeaderWidth(key)
    };
  }

  protected getTableHeaderWidth(key: SubmissionTableLabColumnKey | 'action'): number {
    if (key === 'action') {
      return this.tableHeaderWidths()[key] ?? 88;
    }

    return this.tableHeaderWidths()[key] ?? this.table.getColumnWidth(key);
  }

  protected updateUnderwriterSearch(event: Event): void {
    event.stopPropagation();
    this.underwriterSearchTerm.set((event.target as HTMLInputElement)?.value ?? '');
  }

  protected selectUnderwriter(name: string, event: Event): void {
    event.stopPropagation();
    this.table.updateFilter('underwriter', name);
    this.closeUnderwriterMenu();
  }

  protected isUnderwriterSelected(name: string): boolean {
    return this.getColumnFilterValue('underwriter').trim().toLowerCase() === name.trim().toLowerCase();
  }

  protected selectQueueMode(value: QueueMode, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }

    this.table.updateActionFilter(value);
    this.closeQueueModeMenu();
  }

  protected isQueueModeSelected(value: QueueMode): boolean {
    return this.actionFilter() === value;
  }

  protected selectUnderwriterScope(scope: 'mine' | 'all'): void {
    if (!this.canToggleUnderwriterScope()) {
      return;
    }

    const shouldShowAll = scope === 'all';

    if (shouldShowAll === this.showAllSubmissions()) {
      return;
    }

    this.table.toggleUnderwriterScope();
  }

  protected isUnderwriterScopeSelected(scope: 'mine' | 'all'): boolean {
    return scope === 'all' ? this.showAllSubmissions() : !this.showAllSubmissions();
  }

  protected toggleSubmissionStatusOption(option: string, event: Event): void {
    event.stopPropagation();
    this.table.setStatusFilter(option);
  }

  protected isSubmissionStatusSelected(option: string): boolean {
    return this.getFilterValuesForPopover('submissionStatus').includes(option);
  }

  protected toggleApplicationTypeOption(option: string, event: Event): void {
    event.stopPropagation();
    this.table.setApplicationTypeFilter(option);
  }

  protected isApplicationTypeSelected(option: string): boolean {
    return this.getFilterValuesForPopover('applicationType').includes(option);
  }

  protected isCheckingDocuments(row: SubmissionTableLabRow): boolean {
    return this.table.isCheckingDocuments(row);
  }

  protected isOpeningSubmission(row: SubmissionTableLabRow): boolean {
    return this.table.isOpeningSubmission(row);
  }

  protected isActionTemporarilyBlocked(row: SubmissionTableLabRow): boolean {
    return this.table.isActionTemporarilyBlocked(row);
  }

  protected isActionDisabled(row: SubmissionTableLabRow): boolean {
    return this.table.isActionDisabled(row);
  }

  protected showActionProgress(row: SubmissionTableLabRow): boolean {
    return this.table.showActionProgress(row);
  }

  protected showActionProcessed(row: SubmissionTableLabRow): boolean {
    return this.table.showActionProcessed(row);
  }

  protected getActionProgressValue(row: SubmissionTableLabRow): number {
    return this.table.getActionProgressValue(row);
  }

  protected getActionProgressStageLabel(row: SubmissionTableLabRow): string {
    return this.table.getActionProgressStageLabel(row);
  }

  protected getActionProgressRingSegments(row: SubmissionTableLabRow) {
    return this.table.getActionProgressRingSegments(row);
  }

  protected getActionCompletedRingSegments(row: SubmissionTableLabRow) {
    return this.table.getActionCompletedRingSegments(row);
  }

  protected getProcessedCheckIcon(row: SubmissionTableLabRow): string {
    return this.table.getProcessedCheckIcon(row);
  }

  protected getCheckColor(row: SubmissionTableLabRow): string {
    return this.table.getCheckColor(row);
  }

  protected getActionTitle(row: SubmissionTableLabRow): string {
    return this.table.getActionTitle(row);
  }

  protected evaluationStatusClasses(value: string | null | undefined): string {
    return this.table.evaluationStatusClasses(value);
  }

  protected getSelectionLabel(key: SubmissionTableLabColumnKey | 'underwriter', fallback = 'Select'): string {
    if (key === 'underwriter') {
      const value = this.getColumnFilterValue('underwriter').trim();
      return value || fallback;
    }

    const values = this.getFilterValuesForPopover(key);

    if (!values.length) {
      return fallback;
    }

    if (values.length <= 2) {
      return values.join(', ');
    }

    return `${values.length} selected`;
  }

  protected getFilterValuesForPopover(key: SubmissionTableLabColumnKey, rawValue?: string): string[] {
    const value = (rawValue ?? this.getColumnFilterValue(key)).trim();

    if (!value) {
      return [];
    }

    if (key === 'submissionStatus' || key === 'applicationType') {
      return value
        .split(',')
        .map((item) => item.trim())
        .filter((item) => item.length > 0);
    }

    return [value];
  }

  protected hasPointerCursor(): boolean {
    return false;
  }

  protected isPropertyListRowClickable(): boolean {
    return false;
  }

  private closeAllMenus(): void {
    this.closeSubmissionStatusMenu();
    this.closeApplicationTypeMenu();
    this.closeUnderwriterMenu();
    this.closeQueueModeMenu();
    this.closeTableColumnsPopover();
  }

  private isClickInsideMenuOrTrigger(target: Node): boolean {
    return [
      this.queueModeTrigger?.nativeElement,
      this.queueModeMenu?.nativeElement,
      this.tableColumnsTrigger?.nativeElement,
      this.tableColumnsPopover?.nativeElement,
      this.submissionStatusTrigger?.nativeElement,
      this.submissionStatusMenu?.nativeElement,
      this.applicationTypeTrigger?.nativeElement,
      this.applicationTypeMenu?.nativeElement,
      this.underwriterTrigger?.nativeElement,
      this.underwriterMenu?.nativeElement
    ].some((element) => element?.contains(target) === true);
  }

  private buildPopoverPosition(
    trigger: HTMLElement | undefined,
    minWidth: number,
    maxWidth: number
  ): MenuPosition | null {
    if (!trigger || typeof window === 'undefined') {
      return null;
    }

    const rect = trigger.getBoundingClientRect();
    const width = Math.min(Math.max(rect.width, minWidth), maxWidth);
    const left = Math.max(12, Math.min(rect.left, window.innerWidth - width - 12));
    const top = rect.bottom + 10;

    return {
      top,
      left,
      width
    };
  }

  private matchesUnderwriterSearch(name: string, query: string): boolean {
    const normalizedName = this.normalizeUnderwriterSearchText(name);

    if (normalizedName.includes(query)) {
      return true;
    }

    const tokens = normalizedName.split(' ').filter((token) => token.length > 0);

    if (!tokens.length) {
      return false;
    }

    const abbreviatedName = [tokens[0], ...tokens.slice(1).map((token) => token.charAt(0))]
      .join(' ')
      .trim();

    return abbreviatedName.includes(query);
  }

  private normalizeUnderwriterSearchText(value: string): string {
    return value
      .toLowerCase()
      .replace(/[()]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }
}
